import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ContentSection } from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import { getVehicleManufacturers } from '../../services/api/miles/miles-services';
import { assertDefinedVoid } from '../../services/common/miscUtils';
import { resetQuoteSpecificReduxData, staticSelector, store } from '../../services/redux';
import {
  btoConfigSelectors,
  setBtoConfigLocked,
  setBtoConfigOptions,
  setBtoConfigPrices,
  setConfigurationString,
  setJatoId,
} from '../../services/redux/features/btoConfig.slice';
import { loadingActions } from '../../services/redux/features/loading.slice';
import { useNavigate } from '../../services/routing/navigate';
import { buildConfigurationString, Configuration } from './buildConfigurationString';
import { ConfigurationValues } from './ConfigurationValues';

const selectedAndNotStandard = (part) => part.isSelected && !part.isStandard;

/**
 * Variable shared with MDT Angular application and updated imperatively without React-specific code.
 */
const crossFrameworkState = {
  configurationCompleted: false,
  configurationLoading: '',
};

const configuratorWrapperId = 'configuratorWrapper';

let _btoStylesLoaded = false;

const loadBtoStylesOnce = async () => {
  if (_btoStylesLoaded) return;

  const sheet = new CSSStyleSheet();

  const [bronsonStylesString, mdtStylesString] = await Promise.all(
    ['assets/bronsonForMdt/bronson-bluelabel/css/style.min.css', 'assets/mdtStyles/cc-style.css'].map((publicPath) =>
      fetch(publicPath).then((res) => res.text())
    )
  );

  const resetStylesString = `#${configuratorWrapperId}{all: initial}`;
  const scopedMdtStylesString = `#${configuratorWrapperId}{${mdtStylesString}}`;

  const stylesString = [resetStylesString, bronsonStylesString, scopedMdtStylesString].join('');

  await sheet.replace(stylesString);

  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];

  _btoStylesLoaded = true;
};

// For each MDT configuration, the user can theoretically navigate back and forth multiple times after reaching the final "Aperçu" (summary) tab.
// Therefore, the critical data (configurationString and configurationOptions) should only be committed to Redux at the final confirmation.
let lastCompletedConfigurationResponse: VWFSAG.Configurator22.Models.IConfigurationResponse | undefined;
let lastCompletedOptions: Array<{
  code: string | undefined;
  priceNet: number;
}>;

const prepareConfigurator = async (
  onNavigateBack: () => void,
  onSubmit,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  cachedConfiguration: Configuration | undefined
) => {
  let allowedManufacturers = [] as string[];
  try {
    const manufacturers = await getVehicleManufacturers();

    allowedManufacturers = manufacturers.map((manufacturer) => {
      if (manufacturer === 'Volkswagen Nutzfahrzeuge') {
        return `${ConfigurationValues.Country}.JATO.${'VWN'}`;
      }

      return `${ConfigurationValues.Country}.JATO.${manufacturer.toUpperCase()}`;
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting vehicle manufacturers', error);
  }
  const proxy = new (window as any).Configurator.Configuration.ConnectorProxy();
  const proxySettings: any = {};

  if (allowedManufacturers.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    proxySettings.onCarlineGroupsLoadingCallback = () => {}; // this method needs to be defined to reach onMakesLoadingCallback method as well
    proxySettings.onMakesLoadingCallback = () => ({
      AllowedMakes: allowedManufacturers,
    });
  }

  proxySettings.ConsumerKey = ConfigurationValues.ConsumerKey; // FR.MILES
  proxySettings.RootElementId = ConfigurationValues.RootElementId; // DOM ID of the element where the Configurator is rendered
  proxySettings.OverviewSummaryUrl = ConfigurationValues.OverviewSummaryUrl;
  proxySettings.ShoppingCartUrl = ConfigurationValues.ShoppingCartUrl;
  proxySettings.UrlRouting = ConfigurationValues.UrlRouting;
  proxySettings.Country = ConfigurationValues.Country; // Catalog data (e.g. the description of models/options)
  proxySettings.Language = ConfigurationValues.Language;
  proxySettings.ShowPriceSpinner = ConfigurationValues.ShowPriceSpinner;
  proxySettings.DisplayGrossPrices = ConfigurationValues.DisplayGrossPrices;
  proxySettings.HideImages = ConfigurationValues.HideImages;
  proxySettings.HideFooter = ConfigurationValues.HideFooter;
  proxySettings.ShowConfigurationComparison = ConfigurationValues.ShowConfigurationComparison;
  proxySettings.SkipSingleMakeSelection = true;
  proxySettings.NavigateBackText = t('step1:navigation:navigateBack'); // Text for the "Navigate Back" button

  proxySettings.ConfigurationChangedAction = {
    onConfigurationCompleted: (
      configurationContainer: Configurator.Configuration.IConfigurationContainer,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isFinished: (configuration: VWFSAG.Configurator22.Models.IConfigurationResponse) => void
    ) => {
      const { configuration } = configurationContainer?.configuration ?? {};
      lastCompletedConfigurationResponse = configurationContainer?.configuration;
      const allOptions = configuration?.parts;
      const filteredOptions = allOptions?.filter(selectedAndNotStandard) || [];
      const exteriorColorOption = filteredOptions?.find(({ type }) => type === 'ExteriorColor');
      const [exteriorColorOptionPackageContent] = exteriorColorOption?.packageContent || [];

      const optionsToSend = filteredOptions.map(({ foreignCode, priceNet }) => ({
        code: foreignCode,
        priceNet,
      }));

      if (exteriorColorOption && exteriorColorOptionPackageContent) {
        const { priceNet, foreignCode } = exteriorColorOptionPackageContent;

        optionsToSend.push({
          code: foreignCode,
          priceNet,
        });

        if (exteriorColorOption.priceNet !== 0 && exteriorColorOptionPackageContent.priceNet === 0) {
          const exteriorColorIndex = optionsToSend.findIndex(
            (option) => option.code === exteriorColorOption.foreignCode
          );
          const exteriorColorPackageIndex = optionsToSend.findIndex(
            (option) => option.code === exteriorColorOptionPackageContent.foreignCode
          );

          optionsToSend[exteriorColorIndex].priceNet = exteriorColorOptionPackageContent.priceNet;
          optionsToSend[exteriorColorPackageIndex].priceNet = exteriorColorOption.priceNet;
        }
      }
      lastCompletedOptions = optionsToSend;
      store.dispatch(
        setBtoConfigPrices({
          grossPriceWithOptions: configuration?.totalPriceGross ?? 0,
          netPriceWithoutOptions: configuration?.priceNet ?? 0,
        })
      );

      const jatoKey = configurationContainer.configuration.configuration?.keys.find((x) => x.keyType === 'JATO');
      const jatoId = jatoKey?.keyValue;

      assertDefinedVoid(jatoId, 'Jato ID is not defined in the configuration response');

      store.dispatch(setJatoId(jatoId));

      crossFrameworkState.configurationCompleted = true;
    },
  };

  proxySettings.onNavigateBackCallback = () => {
    onNavigateBack?.();
  };

  proxySettings.onOverviewLoadedCallback = () => {
    if (!crossFrameworkState.configurationCompleted) {
      store.dispatch(loadingActions.setLoading(true));
    }
  };

  proxySettings.OverviewAction = {
    Label: t('step1:navigation:valider'),
    IsEnabledCallback: () => {
      if (crossFrameworkState.configurationCompleted) {
        store.dispatch(loadingActions.setLoading(false));
      }

      return crossFrameworkState.configurationCompleted;
    },
    OnClickCallback: async () => {
      const minimalConfigurationString = await buildConfigurationString(lastCompletedConfigurationResponse);
      store.dispatch(setConfigurationString({ config: minimalConfigurationString as Configuration }));
      store.dispatch(setBtoConfigOptions(lastCompletedOptions));
      onSubmit?.();

      // Schedule teardown after current macrotask
      setTimeout(() => {
        proxy.destroy();

        const destroyConfigurator = (window as any).Configurator?.Configuration?.ConnectorService?.destroy;

        if (!destroyConfigurator) {
          throw new Error('ConnectorService.destroy necessary to teardown MDT could not be found');
        }

        destroyConfigurator();
      });
    },
  };

  proxy.start(proxySettings, cachedConfiguration);
};

export const BtoConfigurator = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    crossFrameworkState.configurationCompleted = false;
  }, []);

  useEffect(() => {
    const onNavigateBack = () => {
      navigate.selectCarType();
      resetQuoteSpecificReduxData();
    };

    const onSubmit = async () => {
      dispatch(setBtoConfigLocked(true));
      navigate.form(1);
    };

    const initializeConfigurator = async () => {
      const loaderId = dispatch(loadingActions.startContextualLoading()).payload.id;
      const configurationString = staticSelector(btoConfigSelectors.selectConfigurationString) ?? '';
      const previousConfigurationString = staticSelector(btoConfigSelectors.selectPreviousConfigurationString) ?? '';
      let cachedConfiguration: Configuration | undefined;

      try {
        const parsedConfigurationString =
          configurationString.trim().length > 0
            ? (JSON.parse(configurationString) as VWFSAG.Configurator22.Models.IConfigurationResponse)
            : undefined;

        cachedConfiguration = await buildConfigurationString(parsedConfigurationString);

        // Important for Copy/Modify flow: Only write the cached configuration to Redux on the first load (when no previousConfigurationString exists).
        if (cachedConfiguration && !previousConfigurationString) {
          store.dispatch(setConfigurationString({ config: cachedConfiguration as Configuration }));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize configuration: ', error);
      }

      Promise.all([prepareConfigurator(onNavigateBack, onSubmit, cachedConfiguration), loadBtoStylesOnce()]).finally(
        () => {
          dispatch(loadingActions.endContextualLoading(loaderId));
        }
      );
    };

    initializeConfigurator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ContentSection className="u-pv-none">
      <div id="configuratorWrapper" />
    </ContentSection>
  );
};
